import { Injectable, Inject } from '@angular/core';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { filter, takeUntil } from 'rxjs/operators';
import { InteractionStatus, RedirectRequest, AccountInfo } from '@azure/msal-browser';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { UserService, CurrentUserInfoResponse } from './user.service';

/**
 * Azure AD app roles present in the bearer token (idTokenClaims.roles).
 *
 * ADMIN_USERS — Mat Coordinators. Must be in this AD group AND in mat_user table with role = 'Mat Coordinator'.
 *
 * NOTE: Regular Mat Users (Planner, PM, etc.) are NOT assigned any AD app role.
 *       They only need to be present and active in the mat_user table.
 */
export const AppRoles = {
  ADMIN_USERS: 'MatTrack.Admin.Users'
};

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private _user = new BehaviorSubject<any>(null);
  public currentUser$: Observable<any> = this._user.asObservable();

  private readonly _destroying$ = new Subject<void>();
  requiredRoles = {
    read: [AppRoles.ADMIN_USERS],
    write: [AppRoles.ADMIN_USERS]
  };

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private userService: UserService
  ) {}

  updateCurrentUser(user: any): void {
    this._user.next(user);
  }

  init() {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }

    if (activeAccount) {
      const enriched = { ...activeAccount, authorization: this.applyAuthorization(activeAccount) };
      this._user.next(enriched);

      // After MSAL auth passes, fetch backend user info to enforce is_active and share role
      this.userService.getUserInfo().subscribe({
        next: (info: CurrentUserInfoResponse) => {
          this.userService.setCurrentUserInfo(info);
          const user = info?.user;
          // Only redirect if API explicitly says user is inactive
          if (info?.success && user && user.is_active === false) {
            this.router.navigate(['/unauthorized']);
          }
        },
        error: () => {
          // On error, log and continue using MSAL auth only
          console.error('getUserInfo failed; proceeding with MSAL roles only');
          this.userService.setCurrentUserInfo(null);
        }
      });
    } else {
      this.loginRedirect();
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  signOut() {
    this.msalService.logoutRedirect();
  }

  getUser() {
    return this._user.asObservable();
  }

  isAdmin(): boolean {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount?.idTokenClaims && (activeAccount.idTokenClaims as any).roles) {
      const roles = (activeAccount.idTokenClaims as any).roles as string[];
      return roles.includes(AppRoles.ADMIN_USERS);
    }
    return false;
  }

  getUserRoles(): string[] {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount?.idTokenClaims && (activeAccount.idTokenClaims as any).roles) {
      return (activeAccount.idTokenClaims as any).roles as string[];
    }
    return [];
  }

  applyAuthorization(activeAccount: AccountInfo) {
    let authorized = {
      read: true,
      write: false
    };
    
    if (activeAccount && activeAccount.idTokenClaims && (activeAccount.idTokenClaims as any).roles) {
      const roles = (activeAccount.idTokenClaims as any).roles as string[];
      if (roles.filter(value => this.requiredRoles.write.includes(value)).length > 0) {
        authorized.write = true;
        localStorage.setItem('user_access', 'write');
      }
    }

    return authorized;
  }

  destroySubscriptions() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
